import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, MessageCircle } from "lucide-react";
import Navigation from "./Navigation";
import Footer from "./Footer";
import { Reveal, MaskedLine } from "./motion-helpers";
import { usePageMeta } from "./page-meta";
import { PRODUCTS } from "./products";
import { WHATSAPP_URL, WHATSAPP_MSG } from "./constants";

export const FlavourPage = ({ scrollTo }) => {
  const { slug } = useParams();
  const product = PRODUCTS.find((p) => p.slug === slug);

  usePageMeta(
    product
      ? `${product.name} Kombucha — 250 ML Can & 330 ML Bottle | DUNC`
      : "DUNC Kombucha",
    product ? `${product.tasting} Available in 250 ml can and 330 ml bottle.` : undefined
  );

  if (!product) return <Navigate to="/" replace />;

  const next = PRODUCTS.find((p) => p.order === (product.order % 3) + 1);

  return (
    <>
      <Navigation scrollTo={scrollTo} />
      <main
        className="relative min-h-screen overflow-hidden"
        data-testid={`flavour-page-${product.slug}`}
      >
        {/* ambient flavour tint */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background: `radial-gradient(90% 60% at 50% 0%, ${product.accentSoft}, transparent 70%)`,
          }}
        />

        <div className="relative z-10 px-6 pt-32 md:pt-40">
          <div className="mx-auto max-w-7xl">
            <Reveal>
              <Link
                to="/#collection"
                data-testid="flavour-back-link"
                className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#8C8177] transition-colors hover:text-[#D9A441]"
              >
                <ArrowLeft className="h-4 w-4" /> All flavours
              </Link>
            </Reveal>

            {/* Hero */}
            <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <p
                  className="mb-5 text-xs uppercase tracking-[0.4em]"
                  style={{ color: product.accent }}
                >
                  {product.tagline}
                </p>
                <h1 className="font-display font-bold leading-[0.98] text-5xl sm:text-6xl lg:text-7xl text-[#F5EFE6]">
                  <MaskedLine delay={0.1}>{product.name}</MaskedLine>
                </h1>
                <Reveal delay={0.2}>
                  <p className="mt-7 max-w-lg text-base leading-relaxed text-[#CFC6BC]">
                    {product.tasting}
                  </p>
                </Reveal>
                <Reveal delay={0.25}>
                  <div className="mt-7 flex flex-wrap gap-2.5" data-testid="flavour-notes">
                    {product.notes.map((n) => (
                      <span
                        key={n}
                        className="rounded-full border border-white/12 px-4 py-1.5 text-[11px] uppercase tracking-[0.2em] text-[#CFC6BC]"
                      >
                        {n}
                      </span>
                    ))}
                  </div>
                </Reveal>
                <Reveal delay={0.3}>
                  <div className="mt-10 flex flex-col sm:flex-row gap-4">
                    <a
                      href={WHATSAPP_URL(WHATSAPP_MSG.flavour(product.name))}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="flavour-whatsapp-cta"
                      className="inline-flex items-center justify-center gap-3 rounded-full bg-[#D9A441] px-8 py-4 text-[12px] font-medium uppercase tracking-[0.28em] text-[#181310] transition-colors hover:bg-[#E7B35A]"
                      style={{ boxShadow: "0 0 40px rgba(217,164,65,0.20)" }}
                    >
                      <MessageCircle className="h-4 w-4" />
                      Get {product.name}
                    </a>
                    <Link
                      to="/#try"
                      data-testid="flavour-try-cta"
                      className="inline-flex items-center justify-center gap-3 rounded-full border border-white/20 px-8 py-4 text-[12px] font-medium uppercase tracking-[0.28em] text-[#F5EFE6] transition-colors hover:border-[#D9A441]/60 hover:text-[#D9A441]"
                    >
                      Try DUNC first
                    </Link>
                  </div>
                </Reveal>
              </div>

              <Reveal delay={0.15}>
                <div
                  className="relative overflow-hidden rounded-3xl border border-white/8"
                  style={{
                    background: `radial-gradient(120% 90% at 50% 15%, ${product.accentSoft}, transparent 60%), #1a1512`,
                  }}
                >
                  <img
                    src={product.canImage.card}
                    srcSet={product.canImage.srcSet}
                    sizes={product.canImage.sizes}
                    alt={`DUNC ${product.name} sparkling kombucha — 250 ml can`}
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    width="800"
                    height="1418"
                    className="w-full h-full object-cover"
                    data-testid="flavour-hero-image"
                  />
                </div>
              </Reveal>
            </div>

            {/* Formats */}
            <div className="mt-28 md:mt-36" data-testid="flavour-formats">
              <Reveal>
                <p className="mb-6 text-xs uppercase tracking-[0.4em] text-[#D9A441]">
                  Available in
                </p>
              </Reveal>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {product.formats.map((f, i) => (
                  <Reveal key={f.type} delay={i * 0.1}>
                    <div
                      className="rounded-3xl border border-white/8 bg-white/[0.02] p-7 md:p-8"
                      data-testid={`flavour-format-${f.type}`}
                    >
                      <div className="flex items-baseline justify-between gap-4">
                        <h2 className="font-display text-2xl md:text-3xl font-bold text-[#F5EFE6]">
                          {f.volume}
                        </h2>
                        <span
                          className="text-[11px] uppercase tracking-[0.28em]"
                          style={{ color: product.accent }}
                        >
                          {f.type === "can" ? "Can" : "Bottle"}
                        </span>
                      </div>
                      <p className="mt-3 text-[13px] uppercase tracking-[0.24em] text-[#D9A441]">
                        {f.note}
                      </p>
                      <p className="mt-4 text-sm leading-relaxed text-[#CFC6BC]">
                        {f.blurb}
                      </p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>

            {/* Story + Serving */}
            <div className="mt-28 md:mt-36 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
              <Reveal>
                <div>
                  <p className="mb-6 text-xs uppercase tracking-[0.4em] text-[#D9A441]">
                    The Story
                  </p>
                  <h2 className="font-display text-3xl md:text-4xl font-bold leading-[1.05] text-[#F5EFE6]">
                    Why {product.name} exists.
                  </h2>
                  <p className="mt-6 text-base leading-relaxed text-[#CFC6BC]" data-testid="flavour-story">
                    {product.story}
                  </p>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <div>
                  <p className="mb-6 text-xs uppercase tracking-[0.4em] text-[#D9A441]">
                    Serving Suggestion
                  </p>
                  <h2 className="font-display text-3xl md:text-4xl font-bold leading-[1.05] text-[#F5EFE6]">
                    How to drink it.
                  </h2>
                  <p className="mt-6 text-base leading-relaxed text-[#CFC6BC]" data-testid="flavour-serving">
                    {product.serving}
                  </p>
                </div>
              </Reveal>
            </div>

            {/* Ingredients */}
            <div className="mt-28 md:mt-36 pb-32" data-testid="flavour-ingredients">
              <Reveal>
                <p className="mb-6 text-xs uppercase tracking-[0.4em] text-[#D9A441]">
                  Inside the can
                </p>
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="font-display text-3xl md:text-4xl font-bold leading-[1.05] text-[#F5EFE6]">
                  Nothing to hide.
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <ul className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3">
                  {product.ingredients.map((ing) => (
                    <li
                      key={ing}
                      className="border-b border-white/6 pb-3 text-sm text-[#CFC6BC]"
                    >
                      {ing}
                    </li>
                  ))}
                </ul>
              </Reveal>

              {next && (
                <Reveal delay={0.15}>
                  <Link
                    to={`/flavours/${next.slug}`}
                    data-testid="flavour-next-link"
                    className="group mt-20 flex items-center justify-between rounded-3xl border border-white/8 bg-white/[0.02] p-7 md:p-9 transition-colors hover:border-[#D9A441]/40"
                  >
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.28em] text-[#8C8177]">
                        Next flavour
                      </p>
                      <p className="mt-2 font-display text-2xl md:text-3xl font-bold text-[#F5EFE6]">
                        {next.name}
                      </p>
                    </div>
                    <ArrowRight className="h-6 w-6 text-[#D9A441] transition-transform group-hover:translate-x-1" />
                  </Link>
                </Reveal>
              )}
            </div>
          </div>
        </div>
        <Footer scrollTo={scrollTo} />
      </main>
    </>
  );
};

export default FlavourPage;
